import { motion } from 'framer-motion';
import MetaTags from '@/components/MetaTags';

const Eyebrow = ({ children }) => (
  <div className="flex items-center gap-3 mb-4">
    <span className="inline-block w-[20px] h-[2px] bg-[#FF2D78]" />
    <span className="font-subheading uppercase text-xs tracking-[3px]" style={{ color: '#080010' }}>{children}</span>
  </div>
);

const Section = ({ eyebrow, title, children }) => (
  <div className="mb-12">
    <Eyebrow>{eyebrow}</Eyebrow>
    <h2 className="font-subheading text-[20px] uppercase tracking-wide mb-4" style={{ color: '#080010' }}>{title}</h2>
    <div className="font-body text-[15px] leading-[1.85] space-y-4" style={{ color: '#555' }}>
      {children}
    </div>
    <div className="h-[1px] mt-12" style={{ backgroundColor: 'rgba(8,0,16,0.08)' }} />
  </div>
);

const TermsPage = () => {
  return (
    <div className="min-h-screen pt-24" style={{ backgroundColor: '#F5F0EE' }} data-testid="terms-page">
      <MetaTags
        title="Terms & Conditions — Crawl Baby Crawl"
        description="Read the Crawl Baby Crawl terms and conditions for using our services and attending our events."
        path="/terms"
      />
      {/* Hero */}
      <section className="py-16 px-4 relative overflow-hidden" style={{ backgroundColor: '#FF2D78' }}>
        <div className="absolute right-[-40px] top-1/2 -translate-y-1/2 pointer-events-none select-none" aria-hidden="true">
          <span className="font-display text-[280px] md:text-[360px] leading-none uppercase text-white/[0.06]">CBC</span>
        </div>
        <div className="max-w-[760px] mx-auto relative z-10">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-display text-[42px] md:text-[56px] lg:text-[72px] text-white uppercase leading-none"
          >
            Terms & Conditions
          </motion.h1>
          <p className="font-body text-white/70 mt-3 text-sm">Last updated: January 2026</p>
        </div>
      </section>
      <div style={{ height: '5px', backgroundColor: '#B8FF2E' }} />

      {/* Content */}
      <section className="px-4 py-20">
        <div className="max-w-[760px] mx-auto">

          <Section eyebrow="Agreement" title="Acceptance of Terms">
            <p>By accessing or using the Crawl Baby Crawl website, purchasing tickets, or attending any CBC event, you agree to be bound by these Terms & Conditions. If you do not agree to these terms, do not use our site or services.</p>
            <p>These terms apply to all visitors, users, ticket purchasers, and event attendees. We reserve the right to update these terms at any time — continued use of our site after changes are posted means you accept the updated terms.</p>
          </Section>

          <Section eyebrow="Purchases" title="Ticket Purchases & Refunds">
            <p><strong>All ticket purchases are final.</strong> We do not issue refunds for tickets once purchased, regardless of whether you attend the event. Tickets are non-transferable unless otherwise stated at the time of purchase.</p>
            <p><strong>Event cancellation:</strong> If CBC cancels an event for any reason, all ticket holders will receive a full refund to their original payment method. Refunds for cancelled events are processed within 10 business days of the cancellation announcement.</p>
            <p><strong>Event changes:</strong> CBC reserves the right to modify event details including venue, time, route, or included bars. Minor changes do not qualify for refunds. If a significant change occurs, we will notify all ticket holders and offer alternatives where possible.</p>
          </Section>

          <Section eyebrow="Requirements" title="Age Requirement">
            <p><strong>All participants must be 21 years of age or older.</strong> This is non-negotiable. You will be required to present a valid, government-issued photo ID (driver's license, passport, or state ID) at check-in.</p>
            <p>CBC reserves the right to refuse entry to any individual who cannot verify they are 21 or older. No refunds will be issued for inability to verify age at the door.</p>
          </Section>

          <Section eyebrow="Behavior" title="Participant Conduct">
            <p>We throw events that people look forward to. To keep it that way, we have standards.</p>
            <p>CBC reserves the right to remove any participant from an event — without refund — for behavior that is disruptive, dangerous, threatening, discriminatory, or otherwise inappropriate. This includes but is not limited to: excessive intoxication, harassment of other participants or venue staff, destruction of property, or violation of any venue's rules.</p>
            <p>Our event staff and hosts have full authority to make conduct decisions on-site. Their decisions are final.</p>
          </Section>

          <Section eyebrow="Responsibility" title="Alcohol Disclaimer">
            <p>Crawl Baby Crawl promotes responsible drinking. Our events involve visiting bars and consuming alcohol, and we expect all participants to drink responsibly and know their limits.</p>
            <p>CBC is not liable for the actions, decisions, or behavior of any participant before, during, or after our events. By attending a CBC event, you acknowledge that you are voluntarily consuming alcohol and assume all risks associated with doing so.</p>
            <p>We encourage all attendees to arrange safe transportation home. Never drink and drive.</p>
          </Section>

          <Section eyebrow="Liability" title="Limitation of Liability">
            <p>To the fullest extent permitted by law, Crawl Baby Crawl, its owners, officers, employees, and agents shall not be held liable for any personal injury, property damage, loss, theft, or any other damages arising from or related to attendance at a CBC event or use of this website.</p>
            <p>By purchasing a ticket or attending an event, you voluntarily assume all risks associated with participation. CBC events take place at third-party venues, and we are not responsible for the conditions, actions, or negligence of those venues or their staff.</p>
            <p>In no event shall CBC's total liability exceed the amount you paid for your ticket.</p>
          </Section>

          <Section eyebrow="Ownership" title="Intellectual Property">
            <p>All content on the Crawl Baby Crawl website — including but not limited to the CBC name, logo, branding, photography, copy, design, and all related materials — is the property of Crawl Baby Crawl and is protected by applicable copyright and trademark laws.</p>
            <p>You may not reproduce, distribute, modify, display, or use any CBC content without prior written permission from Crawl Baby Crawl. Unauthorized use may result in legal action.</p>
          </Section>

          <Section eyebrow="External" title="Third-Party Links & Services">
            <p>Our website contains links to third-party platforms including Eventbrite, Stripe, and social media channels. These links are provided for your convenience.</p>
            <p>CBC is not responsible for the content, privacy practices, terms, or availability of any third-party websites or services. Accessing third-party links is at your own risk and subject to their respective terms and conditions.</p>
          </Section>

          <Section eyebrow="Legal" title="Governing Law">
            <p>These Terms & Conditions are governed by and construed in accordance with the laws of the State of North Carolina, without regard to its conflict of law provisions. Any disputes arising from these terms or your use of CBC services shall be resolved in the courts of North Carolina.</p>
          </Section>

          <Section eyebrow="Contact" title="Questions">
            <p>If you have questions about these terms, contact us at <a href="mailto:info@crawlbabycrawl.com" className="text-[#FF2D78] hover:underline">info@crawlbabycrawl.com</a>.</p>
          </Section>

          <div className="mt-8">
            <p className="font-body text-sm" style={{ color: '#999' }}>
              Crawl Baby Crawl. All rights reserved.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsPage;
